import { buildOcrAiCurrentReview, validateOcrAiProposal } from "./ocrAiReview";

const normalized = (value) => typeof value === "string" ? value.trim().normalize("NFKC").toLocaleLowerCase().replace(/\s+/g, " ") : "";
const identity = (line) => {
  const description = normalized(line?.description), code = normalized(line?.supplier_code);
  return description || code ? JSON.stringify([description, code]) : null;
};
const counts = (values) => values.reduce((result, value) => result.set(value, (result.get(value) || 0) + 1), new Map());
const retained = (value, previous) => value === null || value === undefined ? previous ?? null : value;

// Stored source indices refer to the draft reviewed then, never to today's row order.
export function prepareSavedOcrAiProposal(savedEntry, currentReview, items = []) {
  const proposal = savedEntry?.proposal;
  if (!proposal?.header || !Array.isArray(proposal.lines) || !Array.isArray(proposal.expenses) || !Array.isArray(proposal.warnings) || proposal.lines.some((line) => !line)) {
    throw new Error("Invalid AI review");
  }
  const baseline = savedEntry.current_review;
  const originalLines = Array.isArray(baseline?.lines) ? baseline.lines : [];
  const originalIndexCounts = counts(proposal.lines.map((line) => line.source_line_index));
  const previousFor = (line) => Number.isInteger(line.source_line_index) && line.source_line_index >= 0 && originalIndexCounts.get(line.source_line_index) === 1
    ? originalLines[line.source_line_index] : null;
  const effectiveLines = proposal.lines.map((line) => {
    const previous = previousFor(line);
    const effective = { ...line };
    // These are the scalar fields the original apply would retain when AI said unknown.
    for (const key of ["description", "supplier_code", "quantity", "unit_price"]) effective[key] = retained(line[key], previous?.[key]);
    return effective;
  });
  const current = buildOcrAiCurrentReview(currentReview, items);
  const currentKeys = current.lines.map(identity), currentCounts = counts(currentKeys);
  const effectiveKeys = effectiveLines.map(identity), effectiveCounts = counts(effectiveKeys);
  const originalKeys = originalLines.map(identity), originalCounts = counts(originalKeys);
  const uniqueCurrentIndex = (key) => key && currentCounts.get(key) === 1 ? currentKeys.indexOf(key) : null;
  const candidates = effectiveLines.map((line, index) => {
    const key = effectiveKeys[index];
    if (!key || effectiveCounts.get(key) !== 1) return null;
    const exact = uniqueCurrentIndex(key);
    if (exact !== null) return exact;
    // A corrected identity may still correspond to an unchanged original row.
    // applyOcrAiProposal will clear that row's item and dependent confirmations.
    const originalKey = identity(previousFor(proposal.lines[index]));
    return originalKey && originalCounts.get(originalKey) === 1 ? uniqueCurrentIndex(originalKey) : null;
  });
  const candidateCounts = counts(candidates);
  const header = { ...proposal.header };
  for (const key of ["supplier", "number", "date"]) header[key] = retained(header[key], baseline?.header?.[key]);
  return validateOcrAiProposal({
    ...proposal,
    header,
    lines: effectiveLines.map((line, index) => ({ ...line, source_line_index: candidateCounts.get(candidates[index]) === 1 ? candidates[index] : null })),
    expenses: proposal.expenses.map((expense) => ({ ...expense })),
    warnings: [...proposal.warnings],
    ...(proposal.totals ? { totals: { ...proposal.totals } } : {}),
  }, current.lines.length);
}
